import { useCallback, useEffect, useState } from 'react'
import { useDashboardClinicId } from './useDashboardPeriod'
import {
  getDashboardSettings,
  type DashboardSettingsMap,
} from '@services/api/dashboard/dashboard-settings.api'
import { buildDefaultDashboardConfigs } from '@components/pages/Settings/Analytics/Dashboard/dashboardSettings.constants'

export type UseDashboardSettingsResult = {
  loading: boolean
  error: string | null
  /** Complete persona-keyed settings map (defaults merged in). */
  settings: DashboardSettingsMap
  clinicId: string | null
  reload: () => void
}

/**
 * Loads the clinic's dashboard settings (defaults merged in by the API layer)
 * for the currently selected clinic. Shared by the settings page and the live
 * dashboard so both read from the same source of truth.
 */
export function useDashboardSettings(): UseDashboardSettingsResult {
  const clinicId = useDashboardClinicId()
  const [settings, setSettings] = useState<DashboardSettingsMap>(() =>
    buildDefaultDashboardConfigs(),
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const reload = useCallback(() => setReloadToken((t) => t + 1), [])

  useEffect(() => {
    if (!clinicId) {
      setError('No clinic selected')
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getDashboardSettings({ clinicId })
      .then((map) => {
        if (cancelled) return
        setSettings(map)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load dashboard settings',
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [clinicId, reloadToken])

  return { loading, error, settings, clinicId, reload }
}
